import React, { useEffect, useState } from "react";
import {
  GET_TASK_BY_ID,
  GET_USER_INFO_FOR_TASKS,
} from "../../../../utils/GraphQL/queries";
import {
  MARK_USER_TASK_COMPLETED,
  UPDATE_USER_TASK,
} from "../../../../utils/GraphQL/mutations";
import "./clientTasksDisplay.css";
import { useLazyQuery, useMutation } from "@apollo/client";
import CycleConfirmationModal from "../CycleConfirmationModal/CycleConfirmationModal";
import BdrModal from "../../../../components/BdrModal/BdrModal";
import BdrButton from "../../../../components/BdrButton/BdrButton";
import TaskDetailFields from "../TaskDetailFields/TaskDetailFields";
import { useSnackbar } from "../../../../utils/GlobalSnackbar/GlobalSnackbarContext";
import { lockBodyScroll, releaseBodyScroll } from "../../../../utils/bodyScrollLock";

const clearTaskObj = {
  id: "",
  assigneeName: "",
  assigneeId: "",
  taskType: "",
  note: "",
  associatedClientId: "",
  associatedClientName: "",
  active: true,
  dateCreated: "",
  dueDate: "",
};

const TaskDisplayModal = ({
  taskId,
  openModal,
  setOpenModal,
  refetch,
  handleUpdatedTasksArr,
}) => {
  const { handleSnack } = useSnackbar();
  const [getTask] = useLazyQuery(GET_TASK_BY_ID, {
    fetchPolicy: "network-only",
  });
  const [updateUserTaskGQLCall] = useMutation(UPDATE_USER_TASK);
  const [taskObj, setTaskObj] = useState({ ...clearTaskObj });
  const [pastDue, setPastDue] = useState(false);
  const [clientAlreadyAssigned, setClientAlreadyAssigned] = useState(false);
  const [taskObjUpdateBool, setTaskObjUpdateBool] = useState(false);
  const [markTaskCompleteGQLCall] = useMutation(MARK_USER_TASK_COMPLETED);
  const [getUsersInfo, { data: usersInfo }] = useLazyQuery(
    GET_USER_INFO_FOR_TASKS,
    { fetchPolicy: "network-only" }
  );
  const [cycleModalOpen, setCycleModalOpen] = useState(false);

  const cycleEditable =
    taskObj?.active === true &&
    taskObj?.assigneeId === "11" &&
    /reminder/i.test(taskObj?.taskType || "");

  /* Lock + release in the cleanup, so the pair is balanced by construction.
     The old shape released on every render where the modal was shut —
     including before it had ever opened — which drives a shared count
     negative. See utils/bodyScrollLock.js. */
  useEffect(() => {
    if (!openModal) return undefined;
    lockBodyScroll();
    return () => releaseBodyScroll();
  }, [openModal]);

  useEffect(() => {
    if (taskId) {
      makeTaskCall();
      if (!usersInfo?.getUsersNamesForTasks) {
        getUsersInfo();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taskId, openModal]);

  /*
   * The effect that used to live here derived the assignee's select index from
   * `taskObj` while depending only on `[usersInfo, openModal]`, and `closeModal`
   * reset the task but never the index — so reopening on a different task
   * showed the PREVIOUS task's assignee. `TaskDetailFields` now derives it with
   * `useMemo`, so there is nothing to keep in sync and nothing to reset.
   */

  const closeModal = (event) => {
    if (event) {
      event.stopPropagation();
    }
    setTaskObj({ ...clearTaskObj });
    setTaskObjUpdateBool(false);
    setPastDue(false);
    setClientAlreadyAssigned(false);
    setOpenModal(false);
  };

  const makeTaskCall = async () => {
    if (taskId) {
      const { data } = await getTask({
        variables: { taskId: taskId },
      });

      if (data?.getTaskById?.id) {
        const pastDue = parseInt(data?.getTaskById?.dueDate) < Date.now();
        setPastDue(pastDue);
        setTaskObj({ ...data?.getTaskById });
        if (data?.getTaskById?.associatedClientId?.length > 0) {
          setClientAlreadyAssigned(true);
        }
      }
    }
  };

  const unMarkTask = async () => {
    const { data } = await markTaskCompleteGQLCall({
      variables: {
        taskId,
        active: true,
      },
    });

    if (data?.markCompletedTask) {
      handleSnack("Task Restored", 3000);
      if (refetch) {
        refetch();
      }

      if (!taskObj?.active) {
        makeTaskCall();
      }

      setOpenModal(true);
      return;
    }
  };

  const markCompleted = async () => {
    const { data } = await markTaskCompleteGQLCall({
      variables: {
        taskId,
        active: false,
      },
    });

    if (data?.markCompletedTask) {
      handleSnack(
        <div className="admin-task-snack">
          <span className="admin-task-item-snack">Task Completed.</span>
          <button
            className="admin-task-snack-btn"
            onClick={() => {
              unMarkTask(taskId);
            }}
          >
            Restore Task?
          </button>
        </div>,
        8000
      );

      if (refetch) {
        refetch();
      }
      setOpenModal(false);
      return;
    }
  };

  const searchResultSet = (result) => {
    setTaskObjUpdateBool(true);
    setTaskObj((prev) => ({
      ...prev,
      associatedClientId: result?._id,
      associatedClientName: `${result?.firstName} ${result?.lastName}`,
    }));
  };

  /* The assignee arrives already resolved to {assigneeId, assigneeName} —
     TaskDetailFields owns the index↔sentinel mapping, so no branch here. */
  const handleAssigneeChange = ({ assigneeId, assigneeName }) => {
    setTaskObjUpdateBool(true);
    setTaskObj((prev) => ({ ...prev, assigneeId, assigneeName }));
  };

  const handleObjChangeForUpdate = async (eventTarget) => {
    setTaskObjUpdateBool(true);
    //event = string when calendar mills updates
    if (typeof eventTarget === "string") {
      setTaskObj((prev) => ({
        ...prev,
        dueDate: eventTarget,
      }));

      return;
    }

    const { name, value } = eventTarget;
    setTaskObj((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleTaskUpdate = async () => {
    const { data } = await updateUserTaskGQLCall({
      variables: {
        ...taskObj,
        prevAssignedClient: clientAlreadyAssigned,
      },
    });

    if (data?.updateUserTask) {
      handleUpdatedTasksArr({ ...data?.updateUserTask });
      handleSnack("Task Updated", 2000);
      closeModal();
      return;
    }

    handleSnack("Something went wrong...", 2000);
  };

  return (
    <>
      {openModal && taskObj?.id !== "" && (
        <BdrModal
          open
          onClose={closeModal}
          variant="panel"
          size="lg"
          /*
           * The shell's header, its close button and its action row — all
           * three used to be hand-rolled here behind `showHeader={false}`,
           * on the stated grounds that the Past Due badge "has no slot" in
           * the shell header. That was not true: `title` takes a node
           * (BdrModal.jsx:286), so the badge sits beside the title exactly
           * the way a status chip does everywhere else, and the modal gets
           * the standard × and the standard footer back.
           */
          title={
            <>
              Task
              {pastDue && (
                <span className="bdr-badge bdr-badge--danger">Past Due</span>
              )}
            </>
          }
          /* `title` is a node, so BdrModal cannot derive the accessible name
             from it (BdrModal.jsx:282) — this is what names the dialog. */
          ariaLabel="Task"
          actions={
            <>
              {cycleEditable && (
                <BdrButton
                  variant="action"
                  onClick={() => setCycleModalOpen(true)}
                >
                  Cycle Position
                </BdrButton>
              )}
              {taskObjUpdateBool && (
                <BdrButton variant="confirm" onClick={handleTaskUpdate}>
                  Update
                </BdrButton>
              )}
              <BdrButton
                variant="confirm"
                onClick={() => {
                  if (taskObj?.active) {
                    markCompleted(taskObj?.id);
                    return;
                  }
                  unMarkTask(taskObj?.id);
                }}
              >
                {taskObj?.active ? "Mark Completed" : "Mark Incomplete"}
              </BdrButton>
            </>
          }
        >
          <TaskDetailFields
            task={taskObj}
            users={usersInfo?.getUsersNamesForTasks}
            onFieldChange={handleObjChangeForUpdate}
            onAssigneeChange={handleAssigneeChange}
            onClientPicked={searchResultSet}
          />
        </BdrModal>
      )}
      <CycleConfirmationModal
        open={cycleModalOpen}
        taskId={taskObj?.id || null}
        onClose={() => setCycleModalOpen(false)}
        onAfterAction={() => {
          if (refetch) refetch();
          makeTaskCall();
        }}
      />
    </>
  );
};

export default TaskDisplayModal;
